import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, TextField, Button, MenuItem,
    Stack, Alert, CircularProgress, IconButton
} from '@mui/material';
import ArrowBackIcon  from '@mui/icons-material/ArrowBack';
import AddIcon        from '@mui/icons-material/Add';
import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios              from '../api/axiosConfig';
import { useAuth }        from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const PARCHMENT  = '#F7F3EE';

const DISTRICT_TYPES = ['CITY', 'TOWN', 'VILLAGE', 'METROPOLIS', 'MEGAPOLIS'];

export default function CreateDistrict() {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const { t }      = useTranslation();

    const [form, setForm]       = useState({ name: '', region: '', address: '', email: '', type: 'CITY' });
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState('');

    // ADMIN only
    useEffect(() => {
        if (user && user.role !== 'ADMIN') navigate('/', { replace: true });
    }, [user, navigate]);

    if (!user || user.role !== 'ADMIN') return null;

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.region.trim() || !form.address.trim() || !form.email.trim()) {
            setError(t('districts.validation', 'Name, region, address and email are required.'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await axios.post('/districts/district', form);
            navigate(`/districts/${res.data.id}`);
        } catch (err) {
            setError(err.response?.data?.message || t('districts.create_failed', 'Failed to create district.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 120, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="sm" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back + title */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
                    <IconButton component="a" href="/districts" size="small"
                        sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="overline"
                            sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                            {t('districts.overline', 'Districts')}
                        </Typography>
                        <Typography variant="h4" fontWeight={900}
                            sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {t('districts.create_title', 'New District')}
                        </Typography>
                    </Box>
                </Stack>

                {/* Form card */}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        bgcolor: 'white', borderRadius: 3, p: 4,
                        boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                        border: '1px solid rgba(196,98,45,0.1)',
                    }}>

                    <Stack spacing={3}>
                        <TextField
                            label={t('districts.name', 'Name')}
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            required fullWidth autoFocus
                            placeholder="e.g. Yerevan Central"
                        />
                        <TextField
                            label={t('districts.region', 'Region')}
                            value={form.region}
                            onChange={e => set('region', e.target.value)}
                            required fullWidth
                            placeholder="e.g. Yerevan"
                        />

                        <TextField
                            label={t('districts.type', 'Type')}
                            value={form.type}
                            onChange={e => set('type', e.target.value)}
                            select required fullWidth>
                            {DISTRICT_TYPES.map(dt => (
                                <MenuItem key={dt} value={dt}>{dt}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={t('districts.address', 'Address')}
                            value={form.address}
                            onChange={e => set('address', e.target.value)}
                            required fullWidth
                            placeholder="e.g. 1 Republic Square"
                        />

                        <TextField
                            label={t('districts.email', 'Email')}
                            value={form.email}
                            onChange={e => set('email', e.target.value)}
                            required fullWidth type="email"
                            placeholder="e.g. district@haytour.am"
                        />

                        {error && <Alert severity="error">{error}</Alert>}

                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button component="a" href="/districts" disabled={saving}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                disabled={saving}
                                sx={{
                                    bgcolor: TERRACOTTA, color: 'white', borderRadius: 2, fontWeight: 700,
                                    boxShadow: '0 4px 12px rgba(196,98,45,0.3)',
                                    '&:hover': { bgcolor: '#9A4A1F' },
                                }}>
                                {saving ? t('common.saving') : t('districts.create_btn', 'Create District')}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}
